import { notify } from "../components/ui";
import type { ApiError, ErrorType } from "../hooks/useApi";
import { RELOGIN_ERRORS } from "../hooks/useAuthenticatedFetch";
import { ERROR_MESSAGES } from "../i18n/errors.pt-BR";
import { t } from "../i18n";
import { SUCCESS_MESSAGES, type SuccessKey } from "../i18n/success.pt-BR";
import { reportError } from "./monitoring";

// Único caminho de feedback do app: todo erro (backend, rede, bug do front)
// vira ApiError → frase do catálogo (ou a do backend) → notify().

// Ouvidos pelo RootLayout: relogin chama o Auth0, upgrade abre o UpgradeModal.
export const RELOGIN_EVENT = "letsdoc:relogin";
export const UPGRADE_EVENT = "letsdoc:upgrade";

const isApiError = (e: unknown): e is ApiError =>
  typeof e === "object" && e !== null && "type" in e && "status" in e;

/** Normaliza qualquer coisa lançada (ou um ErrorType do front) num ApiError. */
export function toAppError(err: unknown): ApiError {
  if (typeof err === "string" && err in ERROR_MESSAGES) {
    return { status: 0, type: err as ErrorType, title: "", detail: "" };
  }
  if (isApiError(err)) return err;
  if (RELOGIN_ERRORS.has((err as { error?: string })?.error ?? "")) {
    return { status: 401, type: "unauthorized", title: "", detail: "" };
  }
  if (err instanceof DOMException && err.name === "TimeoutError") {
    return { status: 0, type: "timeout", title: "", detail: "" };
  }
  // fetch cru (fora do useApi) lança TypeError quando não há resposta.
  if (err instanceof TypeError && /fetch|network|load failed/i.test(err.message)) {
    const type = typeof navigator !== "undefined" && !navigator.onLine ? "network-error" : "server-unreachable";
    return { status: 0, type, title: "", detail: "" };
  }
  return { status: 0, type: "unexpected", title: "", detail: "" };
}

/**
 * Título do catálogo; corpo = frase do backend quando existe (é específica:
 * "Você não pode revogar o seu próprio acesso."), senão a do catálogo.
 */
export function resolveMessage(error: ApiError): { title: string; body: string } {
  const known = ERROR_MESSAGES[error.type];
  const title = known?.title ?? (error.title || ERROR_MESSAGES.unknown.title);
  // 500 genérico: a frase do catálogo, nunca um texto técnico que escape do backend.
  const backendText = error.type === "internal-server-error" ? "" : error.detail;
  let body = backendText || known?.body || ERROR_MESSAGES.unknown.body;
  // 422 com vários campos: o texto é genérico, então aponta o primeiro campo.
  if (error.status === 422 && error.errors && error.errors.length > 1) {
    body = `${body} Confira o campo "${error.errors[0].field}".`;
  }
  return { title, body };
}

export function showError(err: unknown, options?: { retry?: () => void }): void {
  if (err instanceof DOMException && err.name === "AbortError") return; // cancelado de propósito

  const error = toAppError(err);
  reportError(err, error);
  const action = ERROR_MESSAGES[error.type]?.action;

  if (action === "upgrade") {
    window.dispatchEvent(new CustomEvent(UPGRADE_EVENT, { detail: resolveMessage(error).body }));
    return;
  }
  if (action === "relogin") window.dispatchEvent(new Event(RELOGIN_EVENT));

  const { title, body } = resolveMessage(error);
  notify({
    tone: "error",
    title,
    body,
    code: error.status >= 500 ? error.requestId : undefined,
    action: action === "retry" && options?.retry ? { label: t("common.retry"), onClick: options.retry } : undefined,
  });
}

export function showSuccess(key: SuccessKey): void {
  notify({ tone: "success", title: SUCCESS_MESSAGES[key] });
}
